import Highcharts from "highcharts/esm/highcharts.src"

import "highcharts/esm/modules/exporting.src"
import "highcharts/esm/modules/export-data.src"
import "highcharts/esm/modules/accessibility.src"
import "highcharts/esm/modules/gantt.src"

import { useCallback, useMemo } from "react"
import ReactServer from "react-dom/server"
import styled from "styled-components"

import { colors, colorScale } from "@minuthq/meatball-ui-react"
import { addWeeks, subDays } from "date-fns"
import HighchartsReact from "highcharts-react-official"

import { TClockType, TUser } from "src/data/user/user"
import { GanttChartTooltip } from "src/ui/Graphs/GanttChartTooltip"
import {
  TGanttChartSeriesData,
  TGraphZoomOption,
} from "src/ui/Graphs/graphTypes"
import { interpolateColors } from "src/utils/colors"

type GanttChartProps = {
  seriesData: TGanttChartSeriesData[]
  secondaryXAxisOptions?: Highcharts.XAxisOptions & { enabled?: boolean }
  primaryXAxisOptions?: Highcharts.XAxisOptions
  seriesColors?: string[]
  clockType: TClockType
  zoom?: TGraphZoomOption
  height?: number
  containerWidth?: string
  containerHeight?: string
  timezone?: string
  showCurrentDateIndicator?: boolean
  dataLabels?: Highcharts.DataLabelsOptions
  tooltip?: {
    disabled?: boolean
    borderRadius?: Highcharts.TooltipOptions["borderRadius"]
    formatter?: (context: {
      start: Date
      end: Date
      label: string
      description: string
      points?: Highcharts.Point["points"]
    }) => React.ReactElement
  }
}

export function GanttChart({
  seriesData,
  secondaryXAxisOptions = { enabled: true },
  primaryXAxisOptions,
  seriesColors = [
    colors.systemGood,
    colors.systemWarning,
    colors.systemInfo,
    colors.systemError,
  ],
  clockType,
  height,
  zoom = "x",
  tooltip,
  containerWidth,
  containerHeight,
  timezone,
  showCurrentDateIndicator,
  dataLabels,
}: GanttChartProps) {
  const now = Date.now()

  const tooltipFormatter = useCallback(
    function (this: Highcharts.Point) {
      if (!this.x || !this.x2) return false

      const start = new Date(this.x)
      const end = new Date(this.x2)

      const label = this.name
      const desc = this.series.name

      if (tooltip?.formatter) {
        return ReactServer.renderToString(
          tooltip.formatter({
            start,
            end,
            label,
            description: desc,
            points: this.points,
          })
        )
      }

      return ReactServer.renderToString(
        <GanttChartTooltip
          startDate={start.toISOString()}
          endDate={end.toISOString()}
          label={label}
          clockType={clockType}
          timezone={timezone}
        />
      )
    },
    [clockType, timezone, tooltip]
  )

  const series = useMemo<Highcharts.Options["series"]>(() => {
    return seriesData.map(({ data, name, fillColor }, seriesIndex) => {
      const overlapCounter: Record<string, number> = {}

      const baseColors = seriesColors || []
      const repeatedColors = generateRepeatedColors({
        baseColors,
        dataLength: seriesData.length,
      })

      const seriesColor =
        fillColor ??
        getMultiDataColor({
          colors: repeatedColors,
          index: seriesIndex,
          length: seriesData.length,
        })

      const mappedData = data.map((item) => {
        const key = `${item.start}-${item.end}`
        const offset = (overlapCounter[key] = (overlapCounter[key] || 0) + 1)

        return {
          ...item,
          dataLabels: {
            ...dataLabels,
            style: { textOutline: "none" },
          },
          color: seriesColor,
          description: item.name,
          y: seriesIndex + (offset - 1) * 0.5, // Offset to avoid overlap
        }
      })

      return {
        type: "gantt",
        name,
        groupPadding: 0.2,
        showInLegend: true,
        data: mappedData,
      }
    })
  }, [dataLabels, seriesColors, seriesData])

  const options = useMemo<Highcharts.Options>(() => {
    const currentDateIndicator: Highcharts.XAxisOptions["currentDateIndicator"] =
      showCurrentDateIndicator
        ? {
            color: colors.accentPrimary,
            label: {
              format: `${getXAxisDateTimeLabelFormat(clockType).day}, ${clockType === "12" ? "%l:%M %p" : "%H:%M"}`,
              style: axisLabelStyle,
            },
          }
        : undefined

    const sharedXAxisProps: Highcharts.XAxisOptions = {
      type: "datetime",
      lineColor: colors.divider,
      tickColor: colors.divider,
      opposite: true,
    }

    const xAxis: Highcharts.XAxisOptions[] = [
      {
        ...sharedXAxisProps,
        min: subDays(now, 1).getTime(),
        max: addWeeks(now, 2).getTime(),
        tickInterval: 24 * 3600 * 1000, // 1 day
        labels: {
          format: "{value:%e %b}",
          style: axisLabelStyle,
        },
        currentDateIndicator,
        ...primaryXAxisOptions,
      },
    ]

    if (secondaryXAxisOptions?.enabled) {
      xAxis.push({
        ...sharedXAxisProps,
        tickInterval: 7 * 24 * 3600 * 1000, // 1 week
        labels: {
          format: "{value:W%W}",
          style: axisLabelStyle,
        },
        ...secondaryXAxisOptions,
      })
    }

    return {
      accessibility: { enabled: false },
      credits: { enabled: false },
      time: {
        timezone,
      },
      chart: {
        type: "gantt",
        alignTicks: false,
        height,
        zooming: { type: zoom === "none" ? undefined : zoom },
        animation: {
          duration: 300,
        },
      },
      title: { text: "" },
      exporting: {
        enabled: false,
      },
      xAxis,
      yAxis: {
        type: "category",
        categories: series?.map((s) => s.name ?? ""),
        uniqueNames: true,
        max: (series?.length ?? 0) - 1,
        lineWidth: 1,
        lineColor: colors.divider,
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: "{point.name}",
            style: {
              fontWeight: "normal",
              textOverflow: "ellipsis",
            },
          },
        },
      },
      series,
      tooltip: {
        useHTML: true,
        enabled: !tooltip?.disabled,
        formatter: tooltipFormatter,
        padding: 0,
        shadow: false,
        outside: true,
        borderRadius: tooltip?.borderRadius ?? undefined,
      },
    }
  }, [
    showCurrentDateIndicator,
    clockType,
    now,
    primaryXAxisOptions,
    secondaryXAxisOptions,
    timezone,
    height,
    zoom,
    series,
    tooltip?.disabled,
    tooltip?.borderRadius,
    tooltipFormatter,
  ])

  return (
    <ChartWrapper>
      <HighchartsReact
        containerProps={{
          style: {
            minWidth: "800px",
            width: containerWidth || "100%",
            height: containerHeight || "100%",
          },
        }}
        highcharts={Highcharts}
        constructorType="ganttChart"
        options={options}
        immutable={true}
      />
    </ChartWrapper>
  )
}

const ChartWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`

const axisLabelStyle: Highcharts.CSSObject = {
  fontFamily: "'Figtree', sans-serif",
  fontSize: "12px",
  color: colors.textTertiary,
}

function getMultiDataColor({
  colors,
  index,
  length,
}: {
  colors?: string[]
  index: number
  length: number
}): string | undefined {
  const startColor = colorScale.gaff[300]
  const endColor = colorScale.gaff[500]
  const interpolatedColors = interpolateColors(startColor, endColor, length)

  if ((colors?.length ?? 0) > 0) {
    return colors?.[index]
  } else {
    return interpolatedColors[index]
  }
}

/** https://api.highcharts.com/highcharts/xAxis.dateTimeLabelFormats */
const defaultDateTimeLabelFormat = {
  millisecond: "%H:%M:%S.%L",
  second: "%H:%M:%S",
  minute: "%H:%M",
  hour: "%H:%M",
  day: "%e %b",
  week: "%e %b",
  month: "%b '%y",
  year: "%Y",
} as const

function getXAxisDateTimeLabelFormat(
  clockType: TUser["clock_type"]
): Highcharts.AxisDateTimeLabelFormatsOptions {
  const use12hClock = clockType === "12"
  if (use12hClock) {
    return { ...defaultDateTimeLabelFormat, hour: `%I:%M %p`, day: "%a %e %b" }
  }
  return defaultDateTimeLabelFormat
}

function generateRepeatedColors({
  baseColors,
  dataLength,
}: {
  baseColors: string[]
  dataLength: number
}): string[] {
  const colors = Array.from(
    { length: dataLength },
    (_, i) => baseColors[i % baseColors.length]
  ).filter((c) => c !== undefined)

  return colors
}
